import { democracyUrl, apiUrl } from '../../schema/Environment';
import { BusinessErrorCode } from './constants';

export function buildJoinUrl(joinCode) {
    if (!joinCode) return democracyUrl;
    return `${democracyUrl}/?joinCode=${encodeURIComponent(joinCode)}`;
}

export function buildMediaUrl(mediaUrlOrId) {
    if (mediaUrlOrId === null || mediaUrlOrId === undefined) return null;
    if (typeof mediaUrlOrId === 'number') {
        return `${apiUrl}/api/democracy/media/${mediaUrlOrId}`;
    }
    if (mediaUrlOrId.startsWith('http://') || mediaUrlOrId.startsWith('https://')) {
        return mediaUrlOrId;
    }
    return `${apiUrl}/api/democracy${mediaUrlOrId}`;
}

export function secondsToHHMMSS(totalSeconds) {
    if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) {
        return '00:00:00';
    }
    const safe = Math.max(0, Math.floor(totalSeconds));
    const hh = String(Math.floor(safe / 3600)).padStart(2, '0');
    const mm = String(Math.floor((safe % 3600) / 60)).padStart(2, '0');
    const ss = String(safe % 60).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
}

export function hhmmssToSeconds(value) {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return Math.max(0, Math.floor(value));
    const parts = String(value).split(':').map((p) => parseInt(p, 10));
    if (parts.some((n) => Number.isNaN(n))) return 0;
    let hh = 0;
    let mm = 0;
    let ss = 0;
    if (parts.length === 3) {
        [hh, mm, ss] = parts;
    } else if (parts.length === 2) {
        [mm, ss] = parts;
    } else if (parts.length === 1) {
        [ss] = parts;
    }
    return Math.max(0, hh * 3600 + mm * 60 + ss);
}

export function extractErrorMessage(error, fallback) {
    const status = error && error.response && error.response.status;
    const data = error && error.response && error.response.data;
    if (data && data.error) return data.error;
    if (status === 413) return `Файл слишком большой — максимум ${MAX_MEDIA_FILE_MB} МБ`;
    if (error && error.code === 'ECONNABORTED') return 'Превышено время ожидания запроса — повторите попытку';
    if (status === 503) return 'Сервис временно недоступен, повторите попытку';
    if (error && error.message) return error.message;
    return fallback || 'Произошла ошибка';
}

export const MAX_MEDIA_FILE_MB = 500;

// Список расширений file-service (ValidationService) — валидация до отправки.
const ALLOWED_MEDIA_EXTENSIONS = [
    '.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif',
    '.mp4', '.webm', '.mov', '.avi',
    '.mp3', '.aac', '.m4a', '.ogg', '.wav'
];

export function validateMediaFile(file) {
    if (!file) return 'Файл не выбран';
    const name = file.name || '';
    const dot = name.lastIndexOf('.');
    const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
    if (!ALLOWED_MEDIA_EXTENSIONS.includes(ext)) {
        return `Недопустимый тип файла «${name}». Разрешены: ${ALLOWED_MEDIA_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_MEDIA_FILE_MB * 1024 * 1024) {
        return `Файл «${name}» слишком большой — максимум ${MAX_MEDIA_FILE_MB} МБ`;
    }
    return null;
}

export function extractErrorCode(error) {
    const data = error && error.response && error.response.data;
    return data && data.code;
}

export function isBundleIncomplete(error) {
    return extractErrorCode(error) === BusinessErrorCode.BUNDLE_INCOMPLETE;
}

export function copyToClipboard(text) {
    if (!text) return Promise.resolve(false);
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    }
    return Promise.resolve(false);
}

/**
 * Translate the controlled MediaUploaders `value` array into the `MediaRefDTO[]`
 * payload the Update*Request expects.
 *
 * Existing items keep their `id`. Pending placeholders emit `id: null` and
 * a sequential `orderId` (0-based). Backend pairs each `id: null` entry with
 * the next file in the multipart `newFiles` part.
 */
export function valueToMediaRefs(value) {
    const list = Array.isArray(value) ? value : [];
    return list.map((item, index) => ({
        id: item && item.__pending ? null : (item ? item.id : null),
        orderId: index,
    }));
}

/**
 * Build a multipart FormData with two named parts:
 *   - `request`: a Blob carrying the Update*Request JSON (Content-Type application/json,
 *     required for Spring's @RequestPart to deserialize correctly).
 *   - `newFiles`: zero or more File parts, in the same order as the corresponding
 *     `id: null` entries in `request.media` / `request.coverMedia`.
 */
export function buildMultipartRequest(requestPayload, newFiles) {
    const fd = new FormData();
    const requestBlob = new Blob([JSON.stringify(requestPayload)], {
        type: 'application/json',
    });
    fd.append('request', requestBlob);
    (newFiles || []).forEach((file) => {
        fd.append('newFiles', file, file.name);
    });
    return fd;
}

/**
 * Build a single-file multipart FormData with one `file` part.
 *
 * Used by team pass-audio / answer-audio replace and system-sound override.
 */
export function buildSingleFileMultipart(file) {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return fd;
}
